import { isMobile } from "common/utils/browser";
import { getTopPosition } from "common/utils/ui/browser";

// solution is taken from
// https://stackoverflow.com/questions/123999/how-can-i-tell-if-a-dom-element-is-visible-in-the-current-viewport
export const isElementVisible = $el => {
  const rect = $el.getBoundingClientRect();
  const screenWidth = window.innerWidth || document.documentElement.clientWidth;
  const screenHeight = window.innerHeight || document.documentElement.clientHeight;

  // eslint-disable-next-line prettier/prettier
  return rect.bottom > 0 && rect.right > 0 && rect.left < screenWidth && rect.top < screenHeight;
};

// more modern approach, with IntersectionObserver
// https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API

const DEFAULT_OBSERVER_OPTIONS = {
  root: null, // visibility is relative to the browser viewport
  rootMargin: "0px",
  threshold: 1.0 // element must be fully visible
};

/* eslint-disable no-unused-vars */
export const observeElementVisibility = (
  selector,
  callback,
  options = {
    mobileOnly: false,
    once: false,
    observerOptions: {}
  }
) => {
  let scrolled = false;
  let wasSeen = false;

  const $el = typeof selector === "string" ? document.querySelector(selector) : selector;

  const observerOptions = {
    ...DEFAULT_OBSERVER_OPTIONS,
    ...options.observerOptions
  };

  const observerCallback = entries => {
    if (options.mobileOnly && !isMobile()) {
      return; // we are checking for visibility in mobile viewports only
    }

    if (options.once && wasSeen) {
      return; // noop. The callback was already executed once
    }

    const intersectingEntries = entries.filter(entry => entry.isIntersecting);
    if (intersectingEntries.length > 0) {
      // callback(intersectingEntries, scrolled);
      // TODO: cleanup code related to "viewed_on_pdp" Mixpanel event tracking & observeElementVisibility function
      // TODO: https://dekeo.atlassian.net/browse/MN-2113
      wasSeen = true;
    }
    scrolled = true;
  };

  const visibilityObserver = new IntersectionObserver(observerCallback, observerOptions);
  $el && visibilityObserver.observe($el);
  return visibilityObserver;
};

export const waitForElementInViewport = ($el, options = {}) => {
  const $element = $el;
  if (!$element) return Promise.resolve(false);
  if (!document.contains($element)) return Promise.resolve(false);
  if (isElementVisible($element)) return Promise.resolve(true);

  if (typeof window.IntersectionObserver !== "function") {
    return Promise.resolve(true);
  }

  const observerOptions = {
    root: null,
    rootMargin: "0px",
    threshold: 0.2,
    ...options
  };

  return new Promise(resolve => {
    let settled = false;
    let observer = null;

    const settle = value => {
      if (settled) return;
      settled = true;
      if (observer) observer.disconnect();
      resolve(value);
    };

    observer = new window.IntersectionObserver(entries => {
      const entry = entries[0];
      if (!document.contains($element)) {
        settle(false);
        return;
      }
      if (entry?.isIntersecting) {
        settle(true);
      }
    }, observerOptions);

    observer.observe($element);
  });
};

export const observeScrollAnimation = ($elements, options = {}) => {
  if (!$elements?.length) return null;

  const { hiddenClasses = ["opacity-0", "translate-y-4"], threshold = 0.25 } = options;

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const $el = entry.target;
        if (entry.isIntersecting) {
          $el.classList.remove(...hiddenClasses);
        } else if (!isElementVisible($el)) {
          $el.classList.add(...hiddenClasses);
        }
      });
    },
    { ...DEFAULT_OBSERVER_OPTIONS, threshold }
  );

  $elements.forEach($el => observer.observe($el));
  return observer;
};

export const isScrollOnTop = () => getTopPosition() <= 10;

export default isElementVisible;
